import { PayloadNotFound, drop, find, mapMaybe, take } from '@execonline-inc/collections';
import { always, noop } from '@kofno/piper';
import { Maybe, fromEmpty, fromNullable } from 'maybeasy';
import { Error as FromNullableError } from '../../../../../../ErrorHandling';
import { getUpdatedSearchParameters } from '../../../../../../ListFilter';
import { SearchParameter, SearchParameters } from '../../../../../../ListFilter/Types';
import { replaceWhen } from '../../../../../../ReplaceWhen';
import {
  FormTemplate,
  HasFormTemplates,
  MissingFieldError,
  fieldValue,
  findFieldT,
} from '../../../../../../Resource/FormTemplates/Types';
import { Link, Resource } from '../../../../../../Resource/Types';
import { fromNullableT } from '../../../../../../TaskExt';
import {
  ExperienceResource,
  ExperienceResources,
  LearningCollectionResource,
  LearningDesignResource,
  ProductCollection,
  ProductCollectionResource,
  ProductCollectionResources,
  ProductLicenseResource,
  SectionableResource,
  browseAllExperienceStoresFrom,
  browseAllField,
  editableLearningDesignsFrom,
  learningDesignExperienceStoresFrom,
  learningDesignExperienceStoresSelectedIds,
} from '../../../../Types';
import { ShowUnlicensedToggle } from '../../../CurrentCollectionStep/Builder/ExperienceCatalog/Types';
import LearningDesignExperienceStore from '../../../Experience/Store';
import { experienceStore } from '../../../Experience/Types';
import {
  BrowseCatalog,
  LearningCollectionError,
  LearningDesignSection,
  ModifiableExperiences,
  ModifiableExperiencesContext,
  ProductCollectionSection,
  Section,
  SectionContext,
  isSameSection,
} from '../../../Types';
import { FileMetaData } from '../../CurrentCollectionStep/Details/S3ImageUpload/Types';
import IframeContentStore from '../../CurrentCollectionStep/Preview/IframeContent/Store';
import PreviewStore from '../../CurrentCollectionStep/Preview/Store';
import { shaHashGenerator } from '../../CurrentCollectionStep/Preview/Store/Types';
import { LearningCollectionPublishResource } from '../../CurrentCollectionStep/Share/PublishSection/Store/Types';
import { addExperienceStores } from './AddExperienceStores';

export type CommerceType = 'not-available' | 'cc-only' | 'cc-or-direct-bill';
export type PermissionType = 'private' | 'organization' | 'client-groups';

export interface LearningCollectionEditFormPayload {
  kind: 'edit';
  id: number;
  name: string;
  description: string;
  commerceType: CommerceType;
  permissionType: PermissionType;
  clientGroups: number[];
  publishing: LearningCollectionPublishResource;
  productCollections: ProductCollectionResources;
  experiences: ExperienceResources;
  allExperiences: ExperienceResources;
  browseAll: boolean;
  availableExtensionIds: number[];
  browseCatalog: BrowseCatalog;
  selectedScheduledPrograms: number[];
  productLicense: Maybe<ProductLicenseResource>;
  warningStatus: ShowUnlicensedToggle;
}

export type LearningCollectionEditFormResource = Resource<LearningCollectionEditFormPayload> &
  HasFormTemplates;

export interface ProductCollectionLicenseUpdatedPayload {
  allExperiences: ExperienceResources;
}

export type ProductCollectionLicenseUpdatedResource =
  Resource<ProductCollectionLicenseUpdatedPayload>;

// Old urls use "build", and we don't want them to break
type DescribeStep = 'describe' | 'build';

export type LearningCollectionEditableStep = DescribeStep | 'customize' | 'preview' | 'share';

export interface Waiting {
  kind: 'waiting';
  deepLinksSearchParams: SearchParameters;
}

export interface LoadingForDeepLinks {
  kind: 'loading-for-deep-links';
  id: string;
  deepLinksSearchParams: SearchParameters;
}

export interface LoadingForInvitations {
  kind: 'loading-for-invitations';
  id: string;
}

export interface LoadError {
  kind: 'load-error';
  error: LearningCollectionError;
}

export interface ReadyDeepLinks {
  kind: 'ready-deep-links';
  learningCollectionResource: LearningCollectionResource;
  deepLinksSearchParams: SearchParameters;
}

export interface ReadyInvitations {
  kind: 'ready-invitations';
  learningCollectionResource: LearningCollectionResource;
}

export type Visibility = 'show' | 'hide';
export type CustomizeState =
  | Building
  | UpdatingProductLicense
  | EditFormSubmissionError
  | AddingLearningDesign;

export interface CommonState {
  template: FormTemplate;
  selectedProductLicense: Maybe<ProductLicenseResource>;
  browseAllExperiences: ModifiableExperiences;
  celebrationPage: Visibility;
  sections: ReadonlyArray<Section>;
}

export interface EditingDetails extends CommonState {
  kind: 'editing-details';
  productLicenseChangeDialog: Visibility;
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
}

export interface SavingDetails extends CommonState {
  kind: 'saving-details';
  productLicenseChangeDialog: Visibility;
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
  fileMetaData: Maybe<FileMetaData>;
}

export interface Building extends CommonState {
  kind: 'building';
  productLicenseChangeDialog: Visibility;
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
}

export interface ChoosingLearningDesigns extends CommonState {
  kind: 'choosing-learning-designs';
  productLicenseChangeDialog: Visibility;
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
}

export interface AddingLearningDesign extends CommonState {
  kind: 'adding-learning-design';
  productLicenseChangeDialog: Visibility;
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
  newLearningDesignFormTemplate: FormTemplate;
}

export interface DeletingSection extends CommonState {
  kind: 'deleting-section';
  productLicenseChangeDialog: Visibility;
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
  sectionToDelete: Section;
  deleteSectionLink: Link;
}

export interface UpdatingProductLicense extends CommonState {
  kind: 'updating-product-license';
  productLicenseChangeDialog: Visibility;
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
}

export interface Previewing extends CommonState {
  kind: 'previewing';
  productLicenseChangeDialog: Visibility;
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
}

export interface PreviewingUpdating extends CommonState {
  kind: 'previewing-updating';
  previewStore: PreviewStore;
  iframeContentStore: IframeContentStore;
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
}

export interface Sharing extends CommonState {
  kind: 'sharing';
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
}

export interface Finishing extends CommonState {
  kind: 'finishing';
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
}

export interface Finished extends CommonState {
  kind: 'finished';
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
}

export interface Loading {
  kind: 'loading';
  id: string;
  step: LearningCollectionEditableStep;
}

export interface Ready extends CommonState {
  kind: 'ready';
  id: string;
  productLicenseChangeDialog: Visibility;
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
  step: LearningCollectionEditableStep;
}

export interface Updating extends CommonState {
  kind: 'updating';
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
}

export interface EditFormSubmissionError extends CommonState {
  kind: 'edit-form-submission-error';
  productLicenseChangeDialog: Visibility;
  learningCollectionEditFormResource: LearningCollectionEditFormResource;
  error: LearningCollectionError;
}

export type LearningCollectionState =
  | Waiting
  | Ready
  | EditingDetails
  | SavingDetails
  | Building
  | ChoosingLearningDesigns
  | AddingLearningDesign
  | DeletingSection
  | Previewing
  | PreviewingUpdating
  | Sharing
  | Finishing
  | Finished
  | Loading
  | Updating
  | LoadError
  | EditFormSubmissionError
  | LoadingForDeepLinks
  | ReadyDeepLinks
  | LoadingForInvitations
  | ReadyInvitations
  | UpdatingProductLicense;

export type BuildingError = FromNullableError | MissingFieldError;

export type SelectingProductLicenseError = MissingProductLicenseError | PayloadNotFound;
export interface MissingProductLicenseError {
  kind: 'missing-product-license';
}

export const waiting = (deepLinksSearchParams: SearchParameters): Waiting => ({
  kind: 'waiting',
  deepLinksSearchParams,
});

export const loadingForDeepLinks = (
  id: string,
  deepLinksSearchParams: SearchParameters,
): LoadingForDeepLinks => ({
  kind: 'loading-for-deep-links',
  id,
  deepLinksSearchParams,
});

export const loadingForInvitations = (id: string): LoadingForInvitations => ({
  kind: 'loading-for-invitations',
  id,
});

export const loadError = (error: LearningCollectionError): LoadError => ({
  kind: 'load-error',
  error,
});

export const productLicenseChangeDialog = (
  state: Building,
  productLicenseChangeDialogValue: Visibility,
): Building => {
  return {
    ...state,
    productLicenseChangeDialog: productLicenseChangeDialogValue,
  };
};

export const createLearningDesignSection = (
  resource: LearningDesignResource,
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
  sectionTemplate: FormTemplate,
): LearningDesignSection => {
  return {
    kind: 'learning-design-section',
    resource,
    template: sectionTemplate,
    learningDesignExperiences: {
      experienceStores: learningDesignExperienceStoresSelectedIds(
        resource,
        learningCollectionEditFormResource,
      ),
      deletedExperienceStores: calculateDeletedForSection(resource),
    },
  };
};

export const createProductCollectionSection = (
  resource: ProductCollectionResource,
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
  sectionTemplate: FormTemplate,
): ProductCollectionSection => {
  return {
    kind: 'product-collection-section',
    resource,
    template: sectionTemplate,
    learningDesignExperiences: {
      experienceStores: learningDesignExperienceStoresSelectedIds(
        resource,
        learningCollectionEditFormResource,
      ),
      deletedExperienceStores: calculateDeletedForSection(resource),
    },
  };
};

const isLearningDesign = (resource: SectionableResource): resource is LearningDesignResource =>
  resource.payload.kind === 'learning-design';

export const createSection =
  (
    resource: SectionableResource,
    learningCollectionEditFormResource: LearningCollectionEditFormResource,
  ) =>
  (sectionTemplate: FormTemplate): Section => {
    if (isLearningDesign(resource)) {
      return createLearningDesignSection(
        resource,
        learningCollectionEditFormResource,
        sectionTemplate,
      );
    } else {
      return createProductCollectionSection(
        resource,
        learningCollectionEditFormResource,
        sectionTemplate,
      );
    }
  };

const buildSections = (
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
  editableLearningDesigns: ReadonlyArray<SectionableResource>,
): ReadonlyArray<Section> =>
  mapMaybe(
    (resource) =>
      fromNullable(resource.templates.self).map(
        createSection(resource, learningCollectionEditFormResource),
      ),
    editableLearningDesigns,
  );

export const ready = (
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
  template: FormTemplate,
  state: Loading,
): Ready => {
  const { kind, ...previous } = state;
  const editableLearningDesigns = editableLearningDesignsFrom(learningCollectionEditFormResource);
  const sections = buildSections(learningCollectionEditFormResource, editableLearningDesigns);

  return {
    kind: 'ready',
    ...previous,
    learningCollectionEditFormResource: learningCollectionEditFormResource,
    template,
    sections,
    selectedProductLicense: learningCollectionEditFormResource.payload.productLicense,
    browseAllExperiences: {
      experienceStores: browseAllExperienceStoresFrom(learningCollectionEditFormResource),
      deletedExperienceStores: calculateBrowseAllDeleted(
        learningCollectionEditFormResource,
        template,
      ),
    },
    celebrationPage: 'hide',
    productLicenseChangeDialog: 'show',
  };
};

export const readySavingDetails = (
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
  template: FormTemplate,
  state: SavingDetails,
): Ready => {
  const { kind, ...previous } = state;
  const editableLearningDesigns = editableLearningDesignsFrom(learningCollectionEditFormResource);
  const sections = buildSections(learningCollectionEditFormResource, editableLearningDesigns);

  return {
    kind: 'ready',
    ...previous,
    learningCollectionEditFormResource: learningCollectionEditFormResource,
    template,
    sections,
    selectedProductLicense: learningCollectionEditFormResource.payload.productLicense,
    browseAllExperiences: {
      experienceStores: browseAllExperienceStoresFrom(learningCollectionEditFormResource),
      deletedExperienceStores: calculateBrowseAllDeleted(
        learningCollectionEditFormResource,
        template,
      ),
    },
    id: String(learningCollectionEditFormResource.payload.id),
    step: 'customize',
    celebrationPage: 'hide',
    productLicenseChangeDialog: 'show',
  };
};

export const readyDeepLinks = (
  learningCollectionResource: LearningCollectionResource,
  deepLinksSearchParams: SearchParameters,
): ReadyDeepLinks => ({
  kind: 'ready-deep-links',
  learningCollectionResource,
  deepLinksSearchParams,
});

export const updatedSearchParameters = (searchParameter: SearchParameter, state: ReadyDeepLinks) =>
  getUpdatedSearchParameters(
    {
      newSearchParameter: searchParameter,
      searchParameters: state.deepLinksSearchParams,
    },
    'deep_links_filter',
  );

export const readyInvitations = (
  learningCollectionResource: LearningCollectionResource,
): ReadyInvitations => ({
  kind: 'ready-invitations',
  learningCollectionResource,
});

const remove = (activeExperiences: ExperienceResources) => (experience: ExperienceResource) =>
  !activeExperiences.map(({ payload }) => payload.id).includes(experience.payload.id);

const totalExperiences = (payload: ProductCollection): ExperienceResources => {
  switch (payload.kind) {
    case 'ai-recommendation':
    case 'dynamic-recommendation':
    case 'group-coaching':
    case 'recommendation-engine':
      return [];
    case 'learning-design':
      return payload.sourceLearningDesign
        .map(({ payload }) => payload.experiences)
        .getOrElseValue([]);
  }
};
const calculateDeletedForSection = (
  learningDesign: SectionableResource,
): LearningDesignExperienceStore[] => {
  const { payload } = learningDesign;
  return totalExperiences(payload).filter(remove(payload.experiences)).map(experienceStore);
};

const calculateBrowseAllDeleted = (
  resource: LearningCollectionEditFormResource,
  template: FormTemplate,
): LearningDesignExperienceStore[] => {
  return browseAllField(template)
    .map((browseAll) => {
      return !browseAll.value || (browseAll.value && resource.payload.experiences.length === 0)
        ? []
        : resource.payload.allExperiences
            .filter(remove(resource.payload.experiences))
            .map(experienceStore);
    })
    .getOrElseValue([]);
};

export const editingDetails = (state: Ready | Building | SavingDetails): EditingDetails => {
  const { kind, ...previous } = state;
  return {
    kind: 'editing-details',
    ...previous,
  };
};

export const savingDetails = (
  state: EditingDetails,
  fileMetaData: Maybe<FileMetaData>,
): SavingDetails => {
  const { kind, ...previous } = state;
  return {
    kind: 'saving-details',
    ...previous,
    fileMetaData,
  };
};

export const building = (
  state:
    | Previewing
    | Ready
    | ChoosingLearningDesigns
    | SavingDetails
    | AddingLearningDesign
    | DeletingSection,
): Building => {
  const { kind, ...previous } = state;
  return {
    kind: 'building',
    ...previous,
  };
};

export const choosingLearningDesigns = (
  state: Building | AddingLearningDesign | EditFormSubmissionError,
): ChoosingLearningDesigns => {
  const { kind, ...previous } = state;
  return {
    kind: 'choosing-learning-designs',
    ...previous,
  };
};

export const addingLearningDesign = (
  state: ChoosingLearningDesigns | Building,
  newLearningDesignFormTemplate: FormTemplate,
): AddingLearningDesign => {
  const { kind, ...previous } = state;
  return {
    ...previous,
    kind: 'adding-learning-design',
    newLearningDesignFormTemplate,
  };
};

export const deletingSection = (
  state: Building,
  sectionToDelete: Section,
  deleteSectionLink: Link,
): DeletingSection => {
  const { kind, ...previous } = state;
  return {
    ...previous,
    kind: 'deleting-section',
    sectionToDelete,
    deleteSectionLink,
  };
};

const browseAllExperiencesLicenseAdjustment = (
  productCollectionLicenseUpdatedResource: ProductCollectionLicenseUpdatedResource,
  state: UpdatingProductLicense,
) => {
  const experienceStores = productCollectionLicenseUpdatedResource.payload.allExperiences
    .map((experience) => {
      return state.browseAllExperiences.experienceStores.filter((store) =>
        store.represents(experience),
      );
    })
    .flat();

  const deletedExperienceStores = productCollectionLicenseUpdatedResource.payload.allExperiences
    .map(experienceStore)
    .filter((allExperienceStore) =>
      allExperienceStore.representsAny(experienceStores.map((store) => store.experienceResource)),
    );

  return {
    experienceStores,
    deletedExperienceStores,
  };
};

export function modifiableExperiences<T extends CommonState>(
  context: ModifiableExperiencesContext,
  state: T,
): ModifiableExperiences {
  switch (context.kind) {
    case 'browse-all-experiences':
      return state.browseAllExperiences;
    case 'section-context':
      return context.section.learningDesignExperiences;
  }
}

export function replaceSectionInState<T extends CommonState>(state: T, section: Section): T {
  return {
    ...state,
    sections: state.sections.map(replaceWhen(isSameSection(section), always(section))),
  };
}

export function withUpdatedModifiableExperiences<T extends CommonState>(
  context: ModifiableExperiencesContext,
  state: T,
  experiences: ModifiableExperiences,
): T {
  switch (context.kind) {
    case 'browse-all-experiences':
      return {
        ...state,
        browseAllExperiences: experiences,
      };
    case 'section-context':
      return replaceSectionInState(state, {
        ...context.section,
        learningDesignExperiences: experiences,
      });
  }
}

export const buildingFromUpdatingLicense = (
  state: UpdatingProductLicense,
  productCollectionLicenseUpdatedResource: ProductCollectionLicenseUpdatedResource,
): Building => {
  const { kind, ...previous } = state;

  return {
    kind: 'building',
    ...previous,
    browseAllExperiences: browseAllExperiencesLicenseAdjustment(
      productCollectionLicenseUpdatedResource,
      state,
    ),
  };
};

export const updatingProductLicense = (state: Building): UpdatingProductLicense => {
  const { kind, ...previous } = state;
  return {
    kind: 'updating-product-license',
    ...previous,
  };
};

export const previewing = (
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
  state: Sharing | Ready | Updating | PreviewingUpdating,
  template: FormTemplate,
  celebrationPage: Visibility,
): Previewing => {
  const editableLearningDesigns = editableLearningDesignsFrom(learningCollectionEditFormResource);
  const sections = buildSections(learningCollectionEditFormResource, editableLearningDesigns);
  const staticValues: Pick<
    Previewing,
    | 'kind'
    | 'learningCollectionEditFormResource'
    | 'template'
    | 'celebrationPage'
    | 'productLicenseChangeDialog'
    | 'sections'
  > = {
    kind: 'previewing',
    learningCollectionEditFormResource,
    template,
    celebrationPage: celebrationPage,
    productLicenseChangeDialog: 'show',
    sections,
  };

  const updateInitialFormHash = (state: Updating | PreviewingUpdating) => {
    switch (state.kind) {
      case 'updating':
        break;
      case 'previewing-updating':
        state.previewStore.updateInitialFormHash(
          shaHashGenerator([
            state.template,
            learningDesignExperienceStoresFrom(editableLearningDesigns),
            browseAllExperienceStoresFrom(learningCollectionEditFormResource),
          ]),
        );
    }
  };

  switch (state.kind) {
    case 'updating':
    case 'previewing-updating':
      updateInitialFormHash(state);
      return {
        ...staticValues,
        selectedProductLicense: state.selectedProductLicense,
        browseAllExperiences: {
          experienceStores: browseAllExperienceStoresFrom(learningCollectionEditFormResource),
          deletedExperienceStores: state.browseAllExperiences.deletedExperienceStores,
        },
      };
    case 'sharing':
    case 'ready':
      return {
        ...staticValues,
        selectedProductLicense: state.selectedProductLicense,
        browseAllExperiences: state.browseAllExperiences,
      };
  }
};

export const sharing = (state: Previewing | Finished | Ready): Sharing => {
  const { kind, ...previous } = state;
  return {
    kind: 'sharing',
    ...previous,
  };
};

export const finishing = (state: Sharing): Finishing => {
  const { kind, ...previous } = state;
  return {
    ...previous,
    kind: 'finishing',
  };
};

export const finished = (state: Finishing): Finished => {
  const { kind, ...previous } = state;
  return {
    kind: 'finished',
    ...previous,
  };
};

export const removeExperienceStore = <T extends CommonState>(
  context: ModifiableExperiencesContext,
  state: T,
  experienceResource: ExperienceResource,
): T => {
  // Temporarily prevent the user from removing the last Broswe All experience.
  // This will be revisited after release.
  switch (context.kind) {
    case 'browse-all-experiences':
      if (state.browseAllExperiences.experienceStores.length === 1) return state;
      break;
    case 'section-context':
      findFieldT('browse_all', 'boolean', context.section.template.fields).fork(noop, (field) => {
        field.value = false;
      });
      break;
  }

  const experienceStores = modifiableExperiences(context, state).experienceStores.filter(
    (store) => !store.represents(experienceResource),
  );
  const deletedExperienceStore = modifiableExperiences(context, state).experienceStores.filter(
    (store) => store.represents(experienceResource),
  );

  const newModifiableExperiences: ModifiableExperiences = {
    experienceStores,
    deletedExperienceStores: modifiableExperiences(context, state).deletedExperienceStores.concat(
      deletedExperienceStore,
    ),
  };

  return withUpdatedModifiableExperiences(context, state, newModifiableExperiences);
};

export const addExperienceStore = <T extends CommonState>(
  context: ModifiableExperiencesContext,
  state: T,
  experienceResource: ExperienceResource,
): T => {
  switch (context.kind) {
    case 'browse-all-experiences':
      break;
    case 'section-context':
      findFieldT('browse_all', 'boolean', context.section.template.fields).fork(noop, (field) => {
        field.value = false;
      });
      break;
  }

  const modifiableExps = modifiableExperiences(context, state);
  const getExperienceStore = fromEmpty(
    modifiableExps.deletedExperienceStores.filter((store) => store.represents(experienceResource)),
  ).getOrElse(() => [experienceStore(experienceResource)]);

  const deletedExperienceStores = modifiableExps.deletedExperienceStores.filter(
    (store) => !store.represents(experienceResource),
  );

  const newModifiableExperiences: ModifiableExperiences = {
    experienceStores: modifiableExps.experienceStores.concat(getExperienceStore),
    deletedExperienceStores,
  };

  return withUpdatedModifiableExperiences(context, state, newModifiableExperiences);
};

export const addExperienceStoreWithSelectedIds = <T extends CommonState>(
  context: ModifiableExperiencesContext,
  state: T,
  experienceResource: ExperienceResource,
  selectedIds: number[],
): T => {
  const modifiableExps = modifiableExperiences(context, state);
  const foundOrNewExperienceStore = find(
    (store) => store.represents(experienceResource),
    modifiableExps.deletedExperienceStores,
  ).getOrElse(() => experienceStore(experienceResource));

  foundOrNewExperienceStore.setSelectedIdsForAllLikeThis(selectedIds, state.sections);

  const deletedExperienceStores = modifiableExps.deletedExperienceStores.filter(
    (store) => !store.represents(experienceResource),
  );

  const newModifiableExperiences: ModifiableExperiences = {
    experienceStores: modifiableExps.experienceStores.concat(foundOrNewExperienceStore),
    deletedExperienceStores,
  };

  return withUpdatedModifiableExperiences(context, state, newModifiableExperiences);
};

export const bulkAddAllPrograms = (
  context: SectionContext,
  state: Building,
  experienceResources: ExperienceResource[],
): Building =>
  withUpdatedModifiableExperiences(
    context,
    state,
    addExperienceStores(context, state, experienceResources),
  );

export function toggleAllExperienceStores<T extends CommonState>(
  context: ModifiableExperiencesContext,
  state: T,
): T {
  const combinedExperienceStores: LearningDesignExperienceStore[] = modifiableExperiences(
    context,
    state,
  ).experienceStores.concat(modifiableExperiences(context, state).deletedExperienceStores);

  const newModifiableExperiences: ModifiableExperiences = {
    experienceStores: fromEmpty(modifiableExperiences(context, state).deletedExperienceStores)
      .map(always(combinedExperienceStores))
      .getOrElseValue([]),
    deletedExperienceStores: fromEmpty(
      modifiableExperiences(context, state).deletedExperienceStores,
    )
      .map<LearningDesignExperienceStore[]>(() => [])
      .getOrElseValue(combinedExperienceStores),
  };

  return withUpdatedModifiableExperiences(context, state, newModifiableExperiences);
}

export const loading = (id: string, step: LearningCollectionEditableStep): Loading => {
  return {
    kind: 'loading',
    id,
    step,
  };
};

export const updating = (
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
  state: Building | EditFormSubmissionError,
): Updating => {
  const { kind, ...previous } = state;
  return {
    kind: 'updating',
    ...previous,
    learningCollectionEditFormResource: learningCollectionEditFormResource,
  };
};

export const previewingUpdating = (
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
  state: EditFormSubmissionError | Previewing,
  iframeContentStore: IframeContentStore,
  previewStore: PreviewStore,
): PreviewingUpdating => {
  const { kind, ...previous } = state;
  return {
    kind: 'previewing-updating',
    ...previous,
    iframeContentStore,
    previewStore,
    learningCollectionEditFormResource: learningCollectionEditFormResource,
  };
};

export const discardChanges = (state: Previewing): Previewing => {
  findFieldT('name', 'string', state.template.fields).fork(
    noop,
    (name) => (name.value = state.learningCollectionEditFormResource.payload.name),
  );
  findFieldT('description', 'string', state.template.fields).fork(
    noop,
    (description) =>
      (description.value = state.learningCollectionEditFormResource.payload.description),
  );

  return {
    ...state,
    browseAllExperiences: {
      experienceStores: browseAllExperienceStoresFrom(state.learningCollectionEditFormResource),
      deletedExperienceStores: calculateBrowseAllDeleted(
        state.learningCollectionEditFormResource,
        state.template,
      ),
    },
  };
};

export const editFormSubmissionError = (
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
  template: FormTemplate,
  error: LearningCollectionError,
  state: Updating | PreviewingUpdating,
): EditFormSubmissionError => {
  const { kind, ...previous } = state;
  return {
    kind: 'edit-form-submission-error',
    productLicenseChangeDialog: 'hide',
    ...previous,
    template,
    learningCollectionEditFormResource: learningCollectionEditFormResource,
    error,
  };
};

export const reorder = <T>(list: readonly T[], startIndex: number, endIndex: number): T[] => {
  const result = Array.from(list);
  const removed = result[startIndex];
  const pruned = [...take(startIndex, result), ...drop(startIndex + 1, result)];

  return [...take(endIndex, pruned), removed, ...drop(endIndex, pruned)];
};

export function previewShaHashGenerator(state: Previewing | PreviewingUpdating): string {
  return shaHashGenerator([
    state.template,
    ...state.sections.map((section) => section.template),
    ...state.sections.map((section) => section.learningDesignExperiences.experienceStores),
    state.browseAllExperiences.experienceStores,
  ]);
}

export const findBooleanField = (name: string) => (templateObject: { template: FormTemplate }) =>
  findFieldT(name, 'boolean', templateObject.template.fields);

export const browseAllTemplate = (state: Building | ChoosingLearningDesigns) =>
  fromNullableT(
    state.learningCollectionEditFormResource.templates['create-browse-all-learning-design'],
  );

export const byoTemplate = (state: Building | ChoosingLearningDesigns) =>
  fromNullableT(state.learningCollectionEditFormResource.templates['create-byo-learning-design']);

export const learningDesignTemplate = (state: ChoosingLearningDesigns) =>
  fromNullableT(state.learningCollectionEditFormResource.templates['create-learning-design']);

export const browseAllFieldValue = (template: FormTemplate) =>
  fieldValue('browse_all', 'boolean', template).getOrElseValue(false);
